import { module } from 'modujs'
import { isNil } from '../utils/is'
import { APP_NAME } from '../utils/environment'

/**
 * Returns the given value, optionally passed through the given callback.
 *
 * @param  {*}        value
 * @param  {function} callback
 * @return {*}
 */
const attempt = (value, callback) => {
    if (typeof callback === 'function') {
        if (!isNil(value)) {
            return callback(value)
        }
    }

    return value
}

/**
 * @param  {ValidityState} validity
 * @return {string[]}
 */
const getRelevantValidityAttributes = (validity) => {
    const constraints = []
    for (const constraint in validity) {
        if (validity[constraint]) {
            constraints.push(constraint)
        }
    }

    return constraints
}

/**
 * Determines if the min and max attributes are reversed (high to low).
 *
 * @param  {HTMLInputElement} element
 * @return {boolean}
 */
const hasReversedRange = (element) => {
    const max = Number(element.max || 'NaN')
    const min = Number(element.min || 'NaN')

    if (max.isNaN()) {
        return false
    }

    if (min.isNaN()) {
        return false
    }

    return (max < min)
}

/**
 * Determines if the min and max attributes are reversed (high to low).
 *
 * @param  {HTMLInputElement} element
 * @return {boolean}
 */
const isReversedRangeUnderflowAndOverflow = (element) => {
    if (!hasReversedRange(element)) {
        return false
    }

    const max = Number(element.max || 'NaN')
    const min = Number(element.min || 'NaN')
    const val = Number(element.value || 'NaN')

    if (val.isNaN()) {
        return false
    }

    return (val > max && val < min)
}

/**
 * Determines if the input type is a date/time control.
 *
 * @param  {HTMLInputElement|string} inputType
 * @return {boolean}
 */
const isDateTimeInputType = (inputType) => {
    if (inputType instanceof HTMLInputElement) {
        inputType = inputType.type
    }

    if (typeof inputType !== 'string') {
        return false
    }

    inputType = inputType.toUpperCase()

    if (inputType === 'DATE') {
        return true
    }

    if (inputType === 'DATETIME') {
        return true
    }

    if (inputType === 'DATETIME-LOCAL') {
        return true
    }

    if (inputType === 'TIME') {
        return true
    }

    return false
}

/**
 * Returns the result of the modulo of x by y using a floored division.
 *
 * @param  {number} num1
 * @param  {number} num2
 * @return {number}
 */
const floorModulo = (num1, num2) => {
    return (num1 - num2 * Math.floor(num1 / num2))
}

const getStep = (element) => {
    if (element.step === 'any') {
        return 'any'
    }

    const step = Number(element.step || 1)
    return step <= 0 ? 1 : step
}

/**
 * Retrieves the validation message for {@see ValidityState.patternMismatch}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedPatternMismatchValidationMessage = (element) => {
    if (!element.hasAttribute('data-format')) {
        return null
    }

    let message = window[APP_NAME].l10n['FORM_VALIDATION_PATTERN_MISMATCH_WITH_FORMAT']
    if (!message) {
        return null
    }

    return message.replace('%format%', element.getAttribute('data-format'))
}

/**
 * Retrieves the validation message for {@see ValidityState.rangeOverflow} {@see ValidityState.rangeUnderflow}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedRangeUnderflowAndOverflowValidationMessage = (element) => {
    let message = window[APP_NAME].l10n['FORM_VALIDATION_RANGE_UNDERFLOW_AND_OVERFLOW']
    if (!message) {
        return null
    }

    return message.replace('%lowValue%', element.min).replace('%highValue%', element.max)
}

/**
 * Retrieves the validation message for {@see ValidityState.rangeOverflow}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedRangeOverflowValidationMessage = (element) => {
    let message = isDateTimeInputType(element) && window[APP_NAME].l10n['FORM_VALIDATION_RANGE_OVERFLOW_DATETIME'] || window[APP_NAME].l10n['FORM_VALIDATION_RANGE_OVERFLOW']
    if (!message) {
        return null
    }

    return message.replace('%max%', element.max)
}

/**
 * Retrieves the validation message for {@see ValidityState.rangeUnderflow}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedRangeUnderflowValidationMessage = (element) => {
    let message = isDateTimeInputType(element) && window[APP_NAME].l10n['FORM_VALIDATION_RANGE_UNDERFLOW_DATETIME'] || window[APP_NAME].l10n['FORM_VALIDATION_RANGE_UNDERFLOW']
    if (!message) {
        return null
    }

    return message.replace('%min%', element.min)
}

/**
 * Retrieves the validation message for {@see ValidityState.stepMismatch}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedStepMismatchValidationMessage = (element) => {
    const step = getStep(element)
    if (step === 'any' || step <= 0) {
        return null
    }

    const value     = Number(element.value)
    const maximum   = Number(element.max)
    const minimum   = Number(element.min)

    const stepBase  = (minimum || value || 0)
    const lowValue  = (value - floorModulo((value - stepBase), step))
    const highValue = (value + step - floorModulo((value - stepBase), step))

    let message
    if ((maximum.isNaN() || highValue <= maximum) && lowValue !== highValue && (message = window[APP_NAME].l10n['FORM_VALIDATION_STEP_MISMATCH'])) {
        return message.replace('%lowValue%', lowValue).replace('%highValue%', highValue)
    } else if ((message = window[APP_NAME].l10n['FORM_VALIDATION_STEP_MISMATCH_ONE_VALUE'])) {
        return message.replace('%value%', lowValue)
    }

    return null
}

/**
 * Retrieves the validation message for {@see ValidityState.tooLong}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedTooLongValidationMessage = (element) => {
    let message = window[APP_NAME].l10n['FORM_VALIDATION_TOO_LONG']
    if (!message) {
        return null
    }

    return message.replace('%max%', element.maxlength).replace('%count%', element.value.length)
}

/**
 * Retrieves the validation message for {@see ValidityState.tooShort}.
 *
 * @param  {HTMLElement} element
 * @return {?string}
 */
const getFormattedTooShortValidationMessage = (element) => {
    let message = element.value.length === 1 && window[APP_NAME].l10n['FORM_VALIDATION_TOO_SHORT'] || window[APP_NAME].l10n['FORM_VALIDATION_TOO_LONG']
    if (!message) {
        return null
    }

    return message.replace('%min%', element.minlength).replace('%count%', element.value.length)
}

/**
 * Form Validation
 *
 * Based on {@link https://github.com/javanto/civem.js civem.js}.
 *
 * The checking of `willValidate` is only done during "change", "input" and "invalid"
 * events in case the element is disabled (where `willValidate` would be marked as FALSE).
 * This is required for dynamic forms with conditional fields.
 */
export default class extends module {
    /**
     * Log standard information.
     *
     * @type {boolean}
     */
    DEBUG = false;

    constructor(m) {
        super(m)

        this.mCaptureEvents = [ 'invalid' ];

        this.events = {
            'addedItem': 'onAddedItem',
            'change':    'onChange',
            'invalid':   'onInvalid',
        }
    }

    init() {
        if (!this.hasCustomValidity()) {
            return
        }

        this.DEBUG && console.log('[App.FormValidation] Initializing…')

        this.addEventHandlers(this.el)
    }

    /**
     * @return {boolean}
     */
    hasCustomValidity() {
        return (
            (APP_NAME in window) &&
            ('l10n' in window[APP_NAME]) &&
            ('FORM_VALIDATION_BAD_INPUT' in window[APP_NAME].l10n)
        )
    }

    /**
     * @param {HTMLElement} container - The element containing the form controls.
     */
    addEventHandlers(container) {
        container.querySelectorAll('input,textarea,select').forEach((formControl) => {
            const tagName   = formControl.tagName.toUpperCase()
            const inputType = formControl.type.toUpperCase()

            if (tagName === 'SELECT' || inputType === 'RADIO' || inputType === 'CHECKBOX') {
                formControl.onchange = this.getInputHandler(formControl.onchange)
            } else {
                formControl.oninput = this.getInputHandler(formControl.oninput)
            }

            formControl.oninvalid = this.getInvalidHandler(formControl.oninvalid)
        })
    }

    /**
     * @param  {function} inputCallback - A callback when the listener is called.
     * @return {function} The event listener for "input" or "change".
     */
    getInputHandler(inputCallback) {
        return (event) => {
            const element = event.target

            if (element.willValidate) {
                if (element.type.toUpperCase() === 'RADIO') {
                    var radioGroup = document.getElementsByName(element.name)
                    for (i = 0; i < radioGroup.length; i++) {
                        radioGroup[i].setCustomValidity('')
                    }
                } else {
                    element.setCustomValidity('')
                }
            }

            if (inputCallback) {
                inputCallback(event)
            }

            event.target.checkValidity()
        }
    }

    /**
     * @param  {function} invalidCallback - A callback when the listener is called.
     * @return {function} The event listener for "invalid".
     */
    getInvalidHandler(invalidCallback) {
        return (event) => {
            const element = event.target

            reportValidity(element)

            if (element.willValidate) {
                const validationMessage = reportValidity(element)

                this.DEBUG && console.group(`#${event.target.id}`, '--', getRelevantValidityAttributes(element.validity).join(', '))
                this.DEBUG && console.log('Message:', validationMessage)
                this.DEBUG && console.groupEnd()
            }

            if (invalidCallback) {
                invalidCallback(event)
            }
        }
    }

    /**
     * @param {Event} event - The addedItem event from DuplicateField.
     */
    onAddedItem(event) {
        this.addEventHandlers(event.detail.item)
    }

    /**
     * @param {Event} event - The input event.
     */
    onChange(event) {
        this.sanitizeInputUrl(event.target)
    }

    /**
     * @param {Event} event - The invalid event.
     */
    onInvalid(event) {
        const firstInvalid = this.el.querySelector('input:invalid, select:invalid, textarea:invalid');
        if (event.target !== firstInvalid) {
            return;
        }

        const details = event.target.closest('details');
        if (details && !details.open) {
            details.open = true;
        }

        event.target.focus();
    }

    focusFirstInvalid() {
        const target = this.el.querySelector('input[aria-invalid="true"]:read-only, input:invalid, select:invalid, textarea:invalid');

        const details = target.closest('details');
        if (details && !details.open) {
            details.open = true;
        }

        target.focus();
    }

    /**
     * Sanitizes the URL input element.
     *
     * @param {HTMLInputElement} input - The input element.
     */
    sanitizeInputUrl(input) {
        if (input.type !== 'url') {
            return
        }

        let value = input.value.trim()

        if (value && /^https?:\/\//i.test === false) {
            value = 'http://' + value
        }

        if (input.value !== value) {
            input.value = value
        }
    }

    /**
     * Clears the custom error message on the element.
     *
     * @param {HTMLElement} input - The input element.
     */
    clearCustomValidityOnElement(input) {
        if (input.readOnly) {
            input.setAttribute('aria-invalid', 'false')
        } else if (input.willValidate) {
            input.setCustomValidity('')
            input.checkValidity()
        }
    }

    /**
     * Reports the custom error message on the element.
     *
     * @param {HTMLElement} input     - The input element.
     * @param {string}      [message] - The custom error message.
     */
    reportCustomValidityOnElement(input, message) {
        if (input.readOnly) {
            input.setAttribute('aria-invalid', 'true')
        } else if (input.willValidate) {
            input.setCustomValidity(message)
            input.reportValidity()
        }
    }

    /**
     * Reports the custom error messages from a map of element IDs and messages.
     *
     * @param {Object} errors - The map of errors keyed by element ID.
     */
    reportCustomValidityOnElementsByName(errors) {
        for (const [ inputName, messages ] of Object.entries(errors)) {
            if (!Array.isArray(messages) || !messages.length || !inputName) {
                continue;
            }

            attempt(
                this.el.querySelector(`[name="${inputName}"]`),
                (input) => {
                    console.log('reportCustomValidityOnElementsByName:', input)
                    for (const message of messages) {
                        this.reportCustomValidityOnElement(input, message)
                        /** Reporting only the first error for simplicity */
                        break
                    }
                }
            )
        }
    }
}

/**
 * Returns a boolean value that is false if the element is a candidate for constraint validation,
 * and it does not satisfy its constraints.
 *
 * @param  {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} element
 * @param  {?ValidityState}                                         [validity]
 * @return {boolean}
 */
export function checkValidity(element, validity = null) {
    return (getValidity(element, validity) == null)
}

/**
 * Returns the validation message, if the element is a candidate for constraint validation,
 * and it does not satisfy its constraints.
 *
 * @param  {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} element
 * @param  {?ValidityState}                                         [validity]
 * @return {?string}
 */
export function getValidity(element, validity = null) {
    if (!validity && !element.willValidate) {
        return null
    }

    validity ??= element.validity

    const tagName   = element.tagName.toUpperCase()
    const inputType = element.type.toUpperCase()

    let constraint, formattedErrorMessage, genericErrorMessage

    switch (true) {
        case (validity.badInput):
            switch (true) {
                case (inputType === 'NUMBER'):
                    constraint = 'FORM_VALIDATION_BAD_INPUT_NUMBER'
                    break

                default:
                    constraint = 'FORM_VALIDATION_BAD_INPUT'
                    break
            }
            break

        case (validity.patternMismatch):
            constraint = 'FORM_VALIDATION_PATTERN_MISMATCH'

            formattedErrorMessage = getFormattedPatternMismatchValidationMessage(element)
            break

        case (validity.rangeOverflow && validity.rangeUnderflow):
            formattedErrorMessage = getFormattedRangeUnderflowAndOverflowValidationMessage(element)
            break

        case (validity.rangeOverflow):
            formattedErrorMessage = getFormattedRangeOverflowValidationMessage(element)
            break

        case (validity.rangeUnderflow):
            formattedErrorMessage = getFormattedRangeUnderflowValidationMessage(element)
            break

        case (validity.stepMismatch):
            formattedErrorMessage = getFormattedStepMismatchValidationMessage(element)
            break

        case (validity.tooLong):
            formattedErrorMessage = getFormattedTooLongValidationMessage(element)
            break

        case (validity.tooShort):
            formattedErrorMessage = getFormattedTooShortValidationMessage(element)
            break

        case (validity.typeMismatch):
            switch (true) {
                case (inputType === 'DATE'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_DATE'
                    break

                case (inputType === 'DATETIME'):
                case (inputType === 'DATETIME-LOCAL'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_DATETIME'
                    break

                case (inputType === 'EMAIL'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_EMAIL'
                    break

                case (inputType === 'TEL'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_TEL'
                    break

                case (inputType === 'TIME'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_TIME'
                    break

                case (inputType === 'URL'):
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH_URL'
                    break

                default:
                    constraint = 'FORM_VALIDATION_TYPE_MISMATCH'
                    break
            }
            break

        case (validity.valueMissing):
            switch (true) {
                case (inputType === 'CHECKBOX'):
                    constraint = 'FORM_VALIDATION_VALUE_MISSING_CHECKBOX'
                    break

                case (inputType === 'FILE'):
                    constraint = element.multiple
                        ? 'FORM_VALIDATION_VALUE_MISSING_FILE_MULTIPLE'
                        : 'FORM_VALIDATION_VALUE_MISSING_FILE'
                    break

                case (inputType === 'RADIO'):
                    constraint = 'FORM_VALIDATION_VALUE_MISSING_RADIO'
                    break

                case (tagName === 'SELECT'):
                    constraint = element.multiple
                        ? 'FORM_VALIDATION_VALUE_MISSING_SELECT_MULTIPLE'
                        : 'FORM_VALIDATION_VALUE_MISSING_SELECT'
                    break

                default:
                    constraint = 'FORM_VALIDATION_VALUE_MISSING'
                    break
            }
            break
    }

    if (formattedErrorMessage) {
        return formattedErrorMessage
    } else if (constraint && window[APP_NAME].l10n[constraint]) {
        return window[APP_NAME].l10n[constraint]
    } else if (genericErrorMessage = window[APP_NAME].l10n['FORM_VALIDATION_BAD_INPUT']) {
        return genericErrorMessage
    } else if (element.validationMessage !== '') {
        return element.validationMessage
    }

    return null
}

/**
 * Returns the validation message, if the element is a candidate for constraint validation,
 * and it does not satisfy its constraints.
 *
 * @param  {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} element
 * @param  {?ValidityState}                                         [validity]
 * @return {?string}
 */
export function reportValidity(element, validity = null) {
    const validationMessage = getValidity(element, validity)
    if (!validationMessage) {
        return null;
    }

    if (element.readOnly) {
        element.setAttribute('aria-invalid', 'true')
    } else if (element.willValidate) {
        element.setCustomValidity(validationMessage)

        const details = element.closest('details');
        if (details && !details.open && !details.dataset?.detailsPopoverClose?.includes('outside')) {
            details.open = true;
        }
    }

    return validationMessage
}
